export type WscStreamStatus =
  | 'PENDING'
  | 'SCHEDULED'
  | 'LAUNCHING'
  | 'RUNNING'
  | 'ENDED'
  | 'CANCELED'
  | 'FAILED';

export type WscStreamTrackedEntityType = 'BallOrPuck' | 'Player' | 'Manual' | 'Unknown';

export interface WscStreamCamera {
  id: number;
  uniqueId: string;
  displayName: string | null;
}

export interface WscStream {
  id: number;
  gameId: number;
  // null on auto-director streams — no single camera applies.
  gameCameraId: number | null;
  // True when the player cuts between every camera on the game instead of one.
  autoDirector: boolean;
  // True when scheduled with `wsc: true` (clipro mapping + {home}-NN alias).
  wsc: boolean;
  trackedEntityId: number;
  trackedEntityType: WscStreamTrackedEntityType;
  status: WscStreamStatus;
  instanceId: string | null;
  region: string | null;
  hostname: string | null;
  // WSC {home}-NN alias pointed at hostname; null unless the Route53 upsert succeeded.
  cname: string | null;
  scheduledFor: string | null;
  launchedAt: string | null;
  endedAt: string | null;
  failureReason: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  gameCamera?: WscStreamCamera;
}

export interface WscStreamCreatePayload {
  /** Required unless `autoDirector` is set; the API rejects sending both. */
  gameCameraId?: number;
  autoDirector?: boolean;
  trackedEntityId?: number;
  trackedEntityType?: WscStreamTrackedEntityType;
  /** Also register the game with the WSC (clipro) stream mapping. Best effort server-side. */
  wsc?: boolean;
}

export interface TrackedEntity {
  id: number;
  displayName: string;
  type: WscStreamTrackedEntityType;
  // Player-only fields:
  playerNumber?: string;
  playerPosition?: string;
  playerTeamId?: number;
}

export interface TrackedEntitiesResponse {
  entities: TrackedEntity[];
  homeTeam: { id: number; abbreviation: string; name: string } | null;
  awayTeam: { id: number; abbreviation: string; name: string } | null;
  fallback?: boolean;
}

/**
 * Statuses where the stream is either gone or cannot be acted on. Used to gate
 * the delete button — terminal rows can be removed from view but the API
 * returns 409 if you try to DELETE them again.
 */
export const TERMINAL_WSC_STATUSES: WscStreamStatus[] = ['ENDED', 'CANCELED', 'FAILED'];

/**
 * Statuses where the stream hasn't launched yet, so an operator can start it
 * on demand instead of waiting for the scheduler. Anything else is rejected
 * with a 409 by the launch endpoint.
 */
export const LAUNCHABLE_WSC_STATUSES: WscStreamStatus[] = ['PENDING', 'SCHEDULED'];

/**
 * Statuses where the stream is "in flight" — there is or will be an EC2
 * instance. Used by the UI to color the status badge and decide whether to
 * keep polling.
 */
export const IN_FLIGHT_WSC_STATUSES: WscStreamStatus[] = [
  'PENDING',
  'SCHEDULED',
  'LAUNCHING',
  'RUNNING',
];
